import {
  QueryOperation,
  SearchQuery,
} from "../../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { Dayjs } from "dayjs";
import { Tag } from "antd";
import { createStatusColorMap } from "../../utils/colors";
import {
  MAX_PAGE_SIZE,
  Questionnaire,
  QuestionnaireInput,
  QuestionnaireQuestion,
  QuestionnaireQuestionChoice,
  QuestionnaireQuestionChoiceInput,
  QuestionnaireQuestionChoiceTranslation,
  QuestionnaireQuestionInput,
  QuestionnaireQuestionTranslation,
  QuestionnaireSection,
  QuestionnaireSectionInput,
  QuestionnaireStatus,
} from "../../repository/data.default";
import {
  getQuestionAllChoices,
  getQuestionnaireAllSections,
  getQuestionnaireQuestionChoiceTranslations,
  getQuestionnaireQuestionTranslations,
  getSectionAllQuestions,
} from "../../repository/data.repository";

export interface QuestionnairesForm {
  readonly uid: string;
  readonly name: string;
  readonly type: string;
  readonly description: string;
  readonly createdByUserUserName: string;
  readonly createdOn: Dayjs[];
  readonly status: string;
}

// A Draft is being edited; the Published version is the one delivered; a Retired version was replaced.
export const questionnaireStatusColor = createStatusColorMap<QuestionnaireStatus>({
  Draft: "gold",
  Published: "green",
  Retired: "default",
});

export const QUESTIONNAIRE_STATUSES: QuestionnaireStatus[] = ["Draft", "Published", "Retired"];

export const questionnairesViewColumns: DataColumn[] = [
  {
    title: "Name",
    key: "name",
    width: 192,
    sort: true,
    defaultSort: "asc",
  },
  {
    title: "Version",
    key: "version",
    width: 90,
    sort: true,
    transform: (value: number) => (value ? `v${value}` : ""),
  },
  {
    title: "Status",
    key: "status",
    width: 120,
    sort: true,
    transform: (value: QuestionnaireStatus) => <Tag color={questionnaireStatusColor(value)}>{value}</Tag>,
  },
  {
    title: "Type",
    key: "type",
    width: 192,
    sort: true,
  },
  {
    title: "Description",
    key: "description",
    width: 192,
    sort: true,
  },
  {
    title: "Created by",
    key: "createdByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Created on",
    key: "createdOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Sections count",
    key: "sectionsCount",
    width: 120,
    sort: true,
    type: "badge",
  },
];

const questionnairesQueryMappings: QueryFieldMapping[] = [
  { formField: "name", operation: QueryOperation.Contains },
  { formField: "type", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdByUserUserName", operation: QueryOperation.Contains },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapQuestionnairesViewQuery = (values: QuestionnairesForm): SearchQuery =>
  buildSearchQuery(values, questionnairesQueryMappings);

export const QUESTIONNAIRE_TYPES: QuestionnaireInput["type"][] = [
  "Test",
  "Survey",
  "Roadmap",
];
export const QUESTION_TYPES: QuestionnaireQuestionInput["questionType"][] = [
  "FreeText",
  "SingleChoice",
  "MultipleChoice",
  "Numeric",
];

// Add a mapping for display names
export const QUESTION_TYPE_LABELS: Record<
  QuestionnaireQuestionInput["questionType"],
  string
> = {
  FreeText: "Free Text",
  SingleChoice: "Single Choice",
  MultipleChoice: "Multiple Choice",
  Numeric: "Numeric",
};

export function prepareQuestionnaireData(values: any): QuestionnaireInput {
  return {
    name: values.name,
    description: values.description || "",
    duration: values.duration,
    type: values.type,
  };
}

export function prepareSectionsData(
  sections: any[],
  questionnaireUid: string,
): (QuestionnaireSectionInput & { questionnaireUid: string })[] {
  return sections.map((section) => ({
    name: section.name,
    description: section.description || "",
    order: section.order,
    questionnaireUid,
  }));
}

export function prepareQuestionsData(
  questions: any[],
  sectionUid: string,
): (QuestionnaireQuestionInput & { sectionUid: string })[] {
  return questions.map((question) => ({
    questionType: question.questionType,
    minValue: question.questionType === "Numeric" ? question.minValue : undefined,
    maxValue: question.questionType === "Numeric" ? question.maxValue : undefined,
    unit: question.questionType === "Numeric" ? question.unit : undefined,
    isRequired: !!question.isRequired,
    order: question.order,
    description: question.description,
    sectionUid,
  }));
}

export function prepareChoicesData(
  choices: any[],
  questionUid: string,
): (QuestionnaireQuestionChoiceInput & { questionUid: string })[] {
  return choices.map((choice) => ({
    description: choice.description,
    order: choice.order,
    weight: choice.weight,
    questionUid,
  }));
}

export function prepareQuestionTranslations(
  questionUid: string,
  translations: any[],
) {
  return translations.map((translation) => ({
    translatedDescription: translation.text,
    questionnaireQuestionUid: questionUid,
    languageUid: translation.language,
  }));
}

export function prepareChoiceTranslations(
  choiceUid: string,
  translations: any[],
) {
  return translations.map((translation) => ({
    translatedDescription: translation.text,
    questionnaireQuestionChoiceUid: choiceUid,
    languageUid: translation.language,
  }));
}

export async function fetchQuestionnaireData(
  questionnaireUid: string,
): Promise<{ sections: any[] }> {
  try {
    const sectionsData = await getQuestionnaireAllSections(questionnaireUid);

    const processedSections = await Promise.all(
      sectionsData.data.map(async (section) => {
        const questionsData = await getSectionAllQuestions(section.uid);
        const sortedQuestions = [...questionsData.data].sort(
          (a, b) => a.order - b.order,
        );

        const processedQuestions = await Promise.all(
          sortedQuestions.map(async (question) => {
            // Get question translations
            const questionTranslationsData =
              await getQuestionnaireQuestionTranslations({
                pageNumber: 0,
                pageSize: MAX_PAGE_SIZE,
                searchQuery: {
                  conditions: [
                    {
                      field: "questionnaireQuestionUid",
                      values: [question.uid],
                      operation: QueryOperation.Equal,
                    },
                  ],
                },
              });

            const choicesData = await getQuestionAllChoices(question.uid);
            const sortedChoices = [...choicesData.data].sort(
              (a, b) => a.order - b.order,
            );

            const processedChoices = await Promise.all(
              sortedChoices.map(async (choice) => {
                const choiceTranslationsData =
                  await getQuestionnaireQuestionChoiceTranslations({
                    pageNumber: 0,
                    pageSize: MAX_PAGE_SIZE,
                    searchQuery: {
                      conditions: [
                        {
                          field: "questionnaireQuestionChoiceUid",
                          values: [choice.uid],
                          operation: QueryOperation.Equal,
                        },
                      ],
                    },
                  });

                return {
                  uid: choice.uid,
                  description: choice.description,
                  weight: choice.weight,
                  order: choice.order,
                  rowVersion: choice.rowVersion,
                  translations: choiceTranslationsData.data.map((t) => ({
                    language: t.languageUid,
                    text: t.translatedDescription,
                    uid: t.uid,
                    rowVersion: t.rowVersion,
                  })),
                };
              }),
            );

            return {
              uid: question.uid,
              questionType: question.questionType,
              minValue: question.minValue,
              maxValue: question.maxValue,
              unit: question.unit,
              isRequired: question.isRequired,
              description: question.description,
              order: question.order,
              rowVersion: question.rowVersion,
              choices: processedChoices,
              translations: questionTranslationsData.data.map((t) => ({
                language: t.languageUid,
                text: t.translatedDescription,
                uid: t.uid,
                rowVersion: t.rowVersion,
              })),
            };
          }),
        );

        return {
          uid: section.uid,
          name: section.name,
          description: section.description,
          rowVersion: section.rowVersion,
          questions: processedQuestions,
        };
      }),
    );

    return {
      sections: processedSections,
    };
  } catch (error) {
    console.error("Error fetching questionnaire data:", error);
    throw error;
  }
}

export function prepareUpdateQuestionnaireData(
  values: any,
  initialValues: Questionnaire,
): QuestionnaireInput | undefined {
  // Check if any relevant fields have changed
  const hasChanges =
    values.name !== initialValues.name ||
    values.type !== initialValues.type ||
    (values.description || "") !== (initialValues.description || "") ||
    Number(values.duration) !== Number(initialValues.duration);

  if (!hasChanges) {
    return undefined;
  }

  return {
    uid: initialValues.uid,
    name: values.name,
    type: values.type,
    description: values.description || "",
    duration: values.duration,
    rowVersion: initialValues.rowVersion,
  };
}

export function prepareUpdateSectionData(
  section: any,
  existingSection: Omit<
    QuestionnaireSection,
    "questionsCount" | "questionnaireUid"
  > & {
    questions?: any[];
  },
): QuestionnaireSectionInput | undefined {
  // Check if any relevant fields have changed
  const hasChanges =
    section.name !== existingSection.name ||
    (section.description || "") !== (existingSection.description || "");

  if (!hasChanges) {
    return undefined;
  }

  return {
    uid: existingSection.uid,
    name: section.name,
    description: section.description || "",
    order: section.order,
    rowVersion: existingSection.rowVersion,
  };
}

export function prepareUpdateQuestionData(
  question: any,
  existingQuestion: Omit<
    QuestionnaireQuestion,
    "isRequired" | "sectionUid" | "choicesCount" | "translationsCount"
  > & {
    isRequired?: boolean;
    choices?: Array<
      Omit<QuestionnaireQuestionChoice, "questionUid" | "translationsCount">
    >;
  },
): QuestionnaireQuestionInput | undefined {
  const numeric = question.questionType === "Numeric";

  // Check if any relevant fields have changed
  const hasChanges =
    question.description !== existingQuestion.description ||
    question.questionType !== existingQuestion.questionType ||
    !!question.isRequired !== !!existingQuestion.isRequired ||
    (numeric &&
      (question.minValue !== existingQuestion.minValue ||
        question.maxValue !== existingQuestion.maxValue ||
        (question.unit || "") !== (existingQuestion.unit || "")));

  if (!hasChanges) {
    return undefined;
  }

  return {
    uid: existingQuestion.uid,
    description: question.description,
    questionType: question.questionType,
    minValue: numeric ? question.minValue : undefined,
    maxValue: numeric ? question.maxValue : undefined,
    unit: numeric ? question.unit : undefined,
    isRequired: !!question.isRequired,
    order: question.order,
    rowVersion: existingQuestion.rowVersion,
  };
}

export function prepareUpdateChoiceData(
  choice: any,
  existingChoice: Omit<
    QuestionnaireQuestionChoice,
    "questionUid" | "translationsCount"
  >,
): QuestionnaireQuestionChoiceInput | undefined {
  // Check if any relevant fields have changed
  const hasChanges =
    choice.description !== existingChoice.description ||
    Number(choice.weight) !== Number(existingChoice.weight);

  if (!hasChanges) {
    return undefined;
  }

  return {
    uid: existingChoice.uid,
    description: choice.description,
    weight: choice.weight,
    order: choice.order,
    rowVersion: existingChoice.rowVersion,
  };
}

function findDeletedItems<T extends { uid: string }>(
  initialItems: Array<T & { uid: string }> = [],
  currentItems: Array<{ uid: string }> = [],
): string[] {
  return initialItems
    .filter((item) => !currentItems.some((current) => current.uid === item.uid))
    .map((item) => item.uid);
}

export interface InitialFormValues
  extends Omit<QuestionnaireInput, "uid" | "rowVersion"> {
  sections?: Array<
    Omit<QuestionnaireSection, "questionsCount" | "questionnaireUid"> & {
      questions?: Array<
        Omit<
          QuestionnaireQuestion,
          "isRequired" | "sectionUid" | "choicesCount" | "translationsCount"
        > & {
          choices?: Array<
            Omit<
              QuestionnaireQuestionChoice,
              "questionUid" | "translationsCount"
            > & {
              translations?: Array<{
                uid: string;
                language: string;
                text: string;
                rowVersion: string;
              }>;
            }
          >;
          translations?: Array<{
            uid: string;
            language: string;
            text: string;
            rowVersion: string;
          }>;
        }
      >;
    }
  >;
}

export function findDeletedItemsRecursively(
  initialValues: InitialFormValues | null,
  currentValues: any,
): {
  sections: string[];
  questions: string[];
  choices: string[];
} {
  const deletedItems = {
    sections: [] as string[],
    questions: [] as string[],
    choices: [] as string[],
  };

  if (!initialValues?.sections) return deletedItems;

  // Find deleted sections
  deletedItems.sections = findDeletedItems(
    initialValues.sections,
    currentValues.sections,
  );

  // Find deleted questions and choices within remaining sections
  initialValues.sections.forEach((initialSection) => {
    const currentSection = currentValues.sections?.find(
      (s: any) => s.uid === initialSection.uid,
    );

    if (currentSection && initialSection.questions) {
      // Find deleted questions in this section
      deletedItems.questions.push(
        ...findDeletedItems(initialSection.questions, currentSection.questions),
      );

      // Find deleted choices in remaining questions
      initialSection.questions.forEach((initialQuestion) => {
        const currentQuestion = currentSection.questions?.find(
          (q: any) => q.uid === initialQuestion.uid,
        );

        if (currentQuestion && initialQuestion.choices) {
          deletedItems.choices.push(
            ...findDeletedItems(
              initialQuestion.choices,
              currentQuestion.choices,
            ),
          );
        }
      });
    }
  });

  return deletedItems;
}
